import { useId, useState, type ReactElement, type ReactNode } from "react";
import { cloneElement, isValidElement } from "react";

import { Button } from "../components/Button";
import { Card } from "../components/Display";
import { FieldIdContext, Label } from "../components/FormControls";
import { Icon } from "../components/Icon";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/Overlays";
import { cn } from "../lib/cn";
import { acceptOf, checkFiles, formatBytes, typesOf, type FileRule } from "../lib/files";
import { useUiStrings } from "../lib/strings";

// ── FormField ────────────────────────────────────────────────────────

export interface FormFieldProps {
  label: ReactNode;
  /** The control (Input, Select…). It receives id, aria-invalid and aria-describedby. */
  children: ReactElement<Record<string, unknown>>;
  hint?: ReactNode;
  /** Error message (from Zod or the API's field errors). */
  error?: string | string[] | null;
  required?: boolean;
  className?: string;
  /** Extra element on the label row, e.g. a "Forgot password?" link. */
  labelAside?: ReactNode;
}

/** Label + control + hint + error, wired for accessibility. */
export function FormField({
  label,
  children,
  hint,
  error,
  required,
  className,
  labelAside,
}: FormFieldProps) {
  const id = useId();
  const message = Array.isArray(error) ? error[0] : error;
  const describedBy = [hint ? `${id}-hint` : null, message ? `${id}-error` : null]
    .filter(Boolean)
    .join(" ");
  const control = isValidElement(children)
    ? cloneElement(children, {
        id: (children.props.id as string | undefined) ?? id,
        "aria-invalid": message ? true : undefined,
        "aria-describedby": describedBy || undefined,
        "aria-required": required || undefined,
      })
    : children;
  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={(children.props.id as string | undefined) ?? id}>
          {label}
          {required && (
            <span className="ml-0.5 text-danger" aria-hidden>
              *
            </span>
          )}
        </Label>
        {labelAside}
      </div>
      <FieldIdContext.Provider value={(children.props.id as string | undefined) ?? id}>
        {control}
      </FieldIdContext.Provider>
      {hint && !message && (
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">
          {hint}
        </p>
      )}
      {message && (
        <p id={`${id}-error`} className="flex items-center gap-1 text-xs font-medium text-danger">
          <Icon name="circle-alert" size={14} />
          {message}
        </p>
      )}
    </div>
  );
}

// ── FormSection / FormActions ────────────────────────────────────────

export interface FormSectionProps {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
  /** Title above the content, full width (wide content such as tables); default: title on the left. */
  stacked?: boolean;
}

/** A card with a heading on the left (desktop) and fields on the right. */
export function FormSection({
  title,
  description,
  children,
  className,
  stacked = false,
}: FormSectionProps) {
  return (
    <Card className={cn("grid gap-6 p-5 sm:p-6", !stacked && "lg:grid-cols-3", className)}>
      <div className="space-y-1">
        <h2 className="text-base font-semibold">{title}</h2>
        {description && <p className="text-sm text-muted-foreground">{description}</p>}
      </div>
      <div className={cn("space-y-4", !stacked && "lg:col-span-2")}>{children}</div>
    </Card>
  );
}

export function FormActions({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-wrap items-center justify-end gap-3", className)}>{children}</div>
  );
}

// ── ConfirmDialog ────────────────────────────────────────────────────

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "default" | "danger";
  /** May return a promise; the button shows a spinner until it settles. */
  onConfirm: () => void | Promise<unknown>;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "default",
  onConfirm,
}: ConfirmDialogProps) {
  const [busy, setBusy] = useState(false);
  async function confirm() {
    setBusy(true);
    try {
      await onConfirm();
      onOpenChange(false);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={busy ? undefined : onOpenChange}>
      <DialogContent hideClose>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            variant={tone === "danger" ? "danger" : "primary"}
            onClick={confirm}
            loading={busy}
          >
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export interface ConfirmRequest {
  title: ReactNode;
  description?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: "default" | "danger";
  /** Runs only after the person confirms; may return a promise. Errors are the caller's to show (e.g. a toast). */
  onConfirm: () => void | Promise<unknown>;
}

/**
 * Ask before doing something that can't be undone (every delete):
 *
 *     const confirm = useConfirm();
 *     <Button onClick={() => confirm.ask({ title: "Delete X?", tone: "danger", confirmLabel: "Delete", onConfirm: remove })} />
 *     {confirm.dialog}
 */
export function useConfirm(): { ask: (request: ConfirmRequest) => void; dialog: ReactNode } {
  const [request, setRequest] = useState<ConfirmRequest | null>(null);
  const dialog = (
    <ConfirmDialog
      open={request !== null}
      onOpenChange={(open) => {
        if (!open) setRequest(null);
      }}
      title={request?.title ?? ""}
      description={request?.description}
      confirmLabel={request?.confirmLabel}
      cancelLabel={request?.cancelLabel}
      tone={request?.tone ?? "danger"}
      onConfirm={async () => {
        try {
          await request?.onConfirm();
        } catch {
          // the caller reports the failure (e.g. a toast from its mutation); the dialog closes either way
        }
      }}
    />
  );
  return { ask: setRequest, dialog };
}

// ── FileDropzone ─────────────────────────────────────────────────────

export interface FileDropzoneProps {
  /** The files that passed the checks (type and size). */
  onFiles: (files: File[]) => void;
  /** What may be uploaded (`FILE_RULES.image`, …): sets `accept` and `maxBytes` together. */
  rule?: FileRule;
  accept?: string;
  /** Largest size of one file. */
  maxBytes?: number;
  /** Called with the refused files' messages; without it they are listed under the box. */
  onRefused?: (messages: string[]) => void;
  multiple?: boolean;
  disabled?: boolean;
  title?: ReactNode;
  hint?: ReactNode;
  className?: string;
  /** Id of the file input (a surrounding `FormField` label points at it). */
  id?: string;
}

/**
 * Drop files here or click to pick them. Dropped and picked files are both checked against the allowed types and
 * the size limit before `onFiles` sees them (dropping ignores the picker's filter, so this matters), and each refused
 * file gets a clear message. The server checks again, including what is really inside the file.
 */
export function FileDropzone({
  onFiles,
  rule,
  accept = rule ? acceptOf(rule) : undefined,
  maxBytes = rule?.maxBytes,
  onRefused,
  multiple = true,
  disabled,
  title = "Drag files here or click to browse",
  hint,
  className,
  id,
}: FileDropzoneProps) {
  const strings = useUiStrings();
  const [dragging, setDragging] = useState(false);
  const [refusals, setRefusals] = useState<string[]>([]);
  const ownId = useId();
  const inputId = id ?? ownId;

  const take = (picked: File[]) => {
    const files = multiple ? picked : picked.slice(0, 1);
    const { accepted, refused } = checkFiles(files, { accept, maxBytes, rule });
    const types = rule ? typesOf(rule) : (accept ?? "");
    const messages = refused.map(({ file, reason, limit }) =>
      reason === "size"
        ? strings.fileTooLarge(file.name, formatBytes(limit ?? 0))
        : strings.fileType(file.name, types),
    );
    if (onRefused) onRefused(messages);
    else setRefusals(messages);
    if (accepted.length) onFiles(accepted);
  };

  return (
    <div className={className}>
      <label
        htmlFor={inputId}
        onDragOver={(event) => {
          event.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          if (!disabled) take(Array.from(event.dataTransfer.files));
        }}
        className={cn(
          "flex cursor-pointer flex-col items-center gap-2 rounded-card border-2 border-dashed border-input bg-surface px-6 py-10 text-center transition-colors",
          dragging && "border-brand bg-brand-soft",
          disabled && "cursor-not-allowed opacity-60",
        )}
      >
        <span className="flex size-12 items-center justify-center rounded-full bg-brand-soft text-brand-soft-foreground">
          <Icon name="upload" size={22} />
        </span>
        <span className="text-sm font-semibold">{title}</span>
        {hint && <span className="text-xs text-muted-foreground">{hint}</span>}
        <input
          id={inputId}
          type="file"
          className="sr-only"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          onChange={(event) => {
            take(Array.from(event.target.files ?? []));
            event.target.value = "";
          }}
        />
      </label>
      {refusals.length > 0 && (
        <ul role="alert" className="mt-2 space-y-1 text-sm text-danger">
          {refusals.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      )}
    </div>
  );
}
